import LocalStorageService from './localStorageService';

/**
 * API-Service als Wrapper für den LocalStorage-Service
 */
const ApiService = {
  /**
   * Lade alle Zahlungsmethoden
   * @returns {Promise<Array>} Promise mit Array von Zahlungsmethoden
   */
  async getPaymentMethods() {
    return Promise.resolve(LocalStorageService.getPaymentMethods());
  },

  /**
   * Zahlungsmethode anhand der ID abrufen
   * @param {number} id - ID der Zahlungsmethode
   * @returns {Promise<Object>} Promise mit der Zahlungsmethode
   */
  async getPaymentMethodById(id) {
    const paymentMethod = LocalStorageService.getPaymentMethodById(id);
    return Promise.resolve(paymentMethod || null);
  },

  /**
   * Neue Zahlungsmethode erstellen
   * @param {Object} paymentMethodData - Daten der neuen Zahlungsmethode
   * @returns {Promise<Object>} Promise mit der erstellten Zahlungsmethode
   */
  async createPaymentMethod(paymentMethodData) {
    // Wenn ein Bild als File-Objekt vorhanden ist, in Base64 umwandeln
    if (paymentMethodData.logo && paymentMethodData.logo instanceof File) {
      const logoBase64 = await LocalStorageService.saveImageAsBase64(paymentMethodData.logo);
      paymentMethodData.logo_path = logoBase64;
      delete paymentMethodData.logo;
    }

    const paymentMethod = LocalStorageService.savePaymentMethod(paymentMethodData);
    return Promise.resolve(paymentMethod);
  },

  /**
   * Zahlungsmethode aktualisieren
   * @param {number} id - ID der zu aktualisierenden Zahlungsmethode
   * @param {Object} paymentMethodData - Aktualisierte Daten
   * @returns {Promise<Object>} Promise mit der aktualisierten Zahlungsmethode
   */
  async updatePaymentMethod(id, paymentMethodData) {
    // Stelle sicher, dass die ID korrekt gesetzt ist
    paymentMethodData.id = parseInt(id);

    // Wenn ein Bild als File-Objekt vorhanden ist, in Base64 umwandeln
    if (paymentMethodData.logo && paymentMethodData.logo instanceof File) {
      const logoBase64 = await LocalStorageService.saveImageAsBase64(paymentMethodData.logo);
      paymentMethodData.logo_path = logoBase64;
      delete paymentMethodData.logo;
    }

    const paymentMethod = LocalStorageService.savePaymentMethod(paymentMethodData);
    return Promise.resolve(paymentMethod);
  },

  /**
   * Zahlungsmethode löschen
   * @param {number} id - ID der zu löschenden Zahlungsmethode
   * @returns {Promise<boolean>} Promise mit Erfolgs-/Misserfolgsindikator
   */
  async deletePaymentMethod(id) {
    const result = LocalStorageService.deletePaymentMethod(id);
    return Promise.resolve(result);
  },

  /**
   * Alle Auszahlungsbedingungen laden
   * @returns {Promise<Array>} Promise mit Array von Auszahlungsbedingungen
   */
  async getPayoutConditions() {
    return Promise.resolve(LocalStorageService.getPayoutConditions());
  },

  /**
   * Auszahlungsbedingung anhand der ID abrufen
   * @param {number} id - ID der Auszahlungsbedingung
   * @returns {Promise<Object>} Promise mit der Auszahlungsbedingung
   */
  async getPayoutConditionById(id) {
    const payoutCondition = LocalStorageService.getPayoutConditionById(id);
    return Promise.resolve(payoutCondition || null);
  },

  /**
   * Neue Auszahlungsbedingung erstellen
   * @param {Object} payoutConditionData - Daten der neuen Auszahlungsbedingung
   * @returns {Promise<Object>} Promise mit der erstellten Auszahlungsbedingung
   */
  async createPayoutCondition(payoutConditionData) {
    const payoutCondition = LocalStorageService.savePayoutCondition(payoutConditionData);
    return Promise.resolve(payoutCondition);
  },

  /**
   * Auszahlungsbedingung aktualisieren
   * @param {number} id - ID der zu aktualisierenden Auszahlungsbedingung
   * @param {Object} payoutConditionData - Aktualisierte Daten
   * @returns {Promise<Object>} Promise mit der aktualisierten Auszahlungsbedingung
   */
  async updatePayoutCondition(id, payoutConditionData) {
    // Stelle sicher, dass die ID korrekt gesetzt ist
    payoutConditionData.id = parseInt(id);

    const payoutCondition = LocalStorageService.savePayoutCondition(payoutConditionData);
    return Promise.resolve(payoutCondition);
  },

  /**
   * Auszahlungsbedingung löschen
   * @param {number} id - ID der zu löschenden Auszahlungsbedingung
   * @returns {Promise<boolean>} Promise mit Erfolgs-/Misserfolgsindikator
   */
  async deletePayoutCondition(id) {
    const result = LocalStorageService.deletePayoutCondition(id);
    return Promise.resolve(result);
  },
  /**
   * Alle Wettbewerber laden
   * @returns {Promise<Array>} Promise mit Array von Wettbewerbern
   */
  async getCompetitors() {
    try {
      const competitors = LocalStorageService.getCompetitors();
      console.log('Geladene Wettbewerber aus LocalStorage:', competitors);
      return Promise.resolve(competitors || []);
    } catch (error) {
      console.error('Fehler beim Laden der Wettbewerber aus LocalStorage:', error);
      return Promise.resolve([]);
    }
  },
  /**
   * Wettbewerber anhand der ID abrufen
   * @param {number} id - ID des Wettbewerbers
   * @returns {Promise<Object>} Promise mit dem Wettbewerber
   */
  async getCompetitorById(id) {
    try {
      const competitor = LocalStorageService.getCompetitorById(id);
      if (!competitor) {
        console.log(`Wettbewerber mit ID ${id} nicht gefunden`);
        return Promise.resolve(null);
      }

      // Füge die Pakete des Wettbewerbers hinzu
      const packages = LocalStorageService.getPackages(id);
      console.log(`Pakete für Wettbewerber ${id} geladen:`, packages);
      return Promise.resolve({
        ...competitor,
        packages: packages || []
      });
    } catch (error) {
      console.error(`Fehler beim Laden des Wettbewerbers ${id}:`, error);
      return Promise.reject(error);
    }
  },

  /**
   * Neuen Wettbewerber erstellen
   * @param {Object} competitorData - Daten des neuen Wettbewerbers
   * @returns {Promise<Object>} Promise mit dem erstellten Wettbewerber
   */
  async createCompetitor(competitorData) {
    // Wenn ein Bild als File-Objekt vorhanden ist, in Base64 umwandeln
    if (competitorData.logo && competitorData.logo instanceof File) {
      const logoBase64 = await LocalStorageService.saveImageAsBase64(competitorData.logo);
      competitorData.logo_path = logoBase64;
      delete competitorData.logo;
    }

    const competitor = LocalStorageService.saveCompetitor(competitorData);
    return Promise.resolve(competitor);
  },

  /**
   * Wettbewerber aktualisieren
   * @param {number} id - ID des zu aktualisierenden Wettbewerbers
   * @param {Object} competitorData - Aktualisierte Daten
   * @returns {Promise<Object>} Promise mit dem aktualisierten Wettbewerber
   */
  async updateCompetitor(id, competitorData) {
    // Stelle sicher, dass die ID korrekt gesetzt ist
    competitorData.id = parseInt(id);

    // Wenn ein Bild als File-Objekt vorhanden ist, in Base64 umwandeln
    if (competitorData.logo && competitorData.logo instanceof File) {
      const logoBase64 = await LocalStorageService.saveImageAsBase64(competitorData.logo);
      competitorData.logo_path = logoBase64;
      delete competitorData.logo;
    }

    const competitor = LocalStorageService.saveCompetitor(competitorData);
    return Promise.resolve(competitor);
  },

  /**
   * Wettbewerber löschen
   * @param {number} id - ID des zu löschenden Wettbewerbers
   * @returns {Promise<boolean>} Promise mit Erfolgs-/Misserfolgsindikator
   */
  async deleteCompetitor(id) {
    const result = LocalStorageService.deleteCompetitor(id);
    return Promise.resolve(result);
  },

  /**
   * Alle Pakete laden
   * @returns {Promise<Array>} Promise mit Array von Paketen
   */
  async getPackages() {
    return Promise.resolve(LocalStorageService.getPackages());
  },

  /**
   * Pakete eines bestimmten Wettbewerbers laden
   * @param {number} competitorId - ID des Wettbewerbers
   * @returns {Promise<Array>} Promise mit Array von Paketen des Wettbewerbers
   */
  async getPackagesByCompetitorId(competitorId) {
    return Promise.resolve(LocalStorageService.getPackages(competitorId));
  },

  /**
   * Paket anhand der ID abrufen
   * @param {number} id - ID des Pakets
   * @returns {Promise<Object>} Promise mit dem Paket
   */
  async getPackageById(id) {
    const pkg = LocalStorageService.getPackageById(id);
    return Promise.resolve(pkg || null);
  },

  /**
   * Neues Paket erstellen
   * @param {Object} packageData - Daten des neuen Pakets
   * @returns {Promise<Object>} Promise mit dem erstellten Paket
   */
  async createPackage(packageData) {
    const pkg = LocalStorageService.savePackage(packageData);
    return Promise.resolve(pkg);
  },

  /**
   * Paket aktualisieren
   * @param {number} id - ID des zu aktualisierenden Pakets
   * @param {Object} packageData - Aktualisierte Daten
   * @returns {Promise<Object>} Promise mit dem aktualisierten Paket
   */
  async updatePackage(id, packageData) {
    // Stelle sicher, dass die ID korrekt gesetzt ist
    packageData.id = parseInt(id);

    const pkg = LocalStorageService.savePackage(packageData);
    return Promise.resolve(pkg);
  },

  /**
   * Paket löschen
   * @param {number} id - ID des zu löschenden Pakets
   * @returns {Promise<boolean>} Promise mit Erfolgs-/Misserfolgsindikator
   */
  async deletePackage(id) {
    const result = LocalStorageService.deletePackage(id);
    return Promise.resolve(result);
  },

  /**
   * Aktuelle Gebühreneinstellung abrufen
   * @returns {Promise<Object>} Promise mit der aktuellen Gebühreneinstellung
   */
  async getCurrentFee() {
    return Promise.resolve(LocalStorageService.getCurrentFee());
  },

  /**
   * Aktuelle Gebühreneinstellung aktualisieren
   * @param {Object} feeData - Aktualisierte Gebühreneinstellung
   * @returns {Promise<Object>} Promise mit der aktualisierten Gebühreneinstellung
   */
  async updateCurrentFee(feeData) {
    const fee = LocalStorageService.saveCurrentFee(feeData);
    return Promise.resolve(fee);
  },

  /**
   * Alle Daten exportieren
   * @returns {Object} Alle gespeicherten Daten als JSON-Objekt
   */  /**
   * Daten selektiv exportieren
   * @param {Object} options - Optionen für den Export (welche Daten exportiert werden sollen)
   * @returns {Object} Die ausgewählten gespeicherten Daten
   */
  exportData(options = {}) {
    return LocalStorageService.exportData(options);
  },
  
  /**
   * Selektiven Export durchführen und als JSON-Datei zum Download bereitstellen
   * @param {Object} options - Optionen für den Export (welche Daten exportiert werden sollen)
   * @returns {string} Die URL der generierten JSON-Datei
   */
  createExportFile(options = {}) {
    const data = this.exportData(options);
    const dataStr = JSON.stringify(data, null, 2);
    const dataUri = "data:application/json;charset=utf-8," + encodeURIComponent(dataStr);
    return dataUri;
  },
  
  /**
   * Daten selektiv importieren
   * @param {Object} data - Die zu importierenden Daten
   * @param {Object} options - Optionen für den Import (welche Daten importiert werden sollen)
   * @returns {boolean} true bei Erfolg, false bei Fehler
   */
  importData(data, options = {}) {
    return LocalStorageService.importData(data, options);
  },
  
  /**
   * Daten auf Standardwerte zurücksetzen
   * @param {Object} options - Optionen für den Reset (welche Daten zurückgesetzt werden sollen)
   * @returns {boolean} true bei Erfolg, false bei Fehler
   */
  resetToDefaults(options = {}) {
    return LocalStorageService.resetToDefaults(options);
  },
  
  /**
   * Alle Daten exportieren (Kompatibilitätsmethode für ältere Versionen)
   * @returns {Object} Alle gespeicherten Daten als JSON-Objekt
   */
  exportAllData() {
    return LocalStorageService.exportAllData();
  },
  
  /**
   * Alle Daten importieren (Kompatibilitätsmethode für ältere Versionen)
   * @param {Object} data - Die zu importierenden Daten
   * @returns {boolean} Erfolgs-/Misserfolgsindikator
   */
  importAllData(data) {
    return LocalStorageService.importAllData(data);
  },
  /**
   * Initialisierung des LocalStorage
   */
  init() {
    LocalStorageService.initializeLocalStorageIfNeeded();
    console.log('API-Service: LocalStorage initialisiert');
  },
  
  /**
   * Bilder als Base64 speichern
   * @param {File} file - Die zu speichernde Datei
   * @returns {Promise<string>} Der Base64-String des Bildes
   */
  async saveImageAsBase64(file) {
    return LocalStorageService.saveImageAsBase64(file);
  }
};

export default ApiService;
